import * as React from "react";
import { AlertTriangle } from "lucide-react";
import { cn } from "../lib/utils.js";

export type PageLoadingProps = {
  message?: React.ReactNode;
  rows?: number;
  className?: string;
};

export function PageLoading({
  message = "Loading…",
  rows = 4,
  className,
}: PageLoadingProps): React.ReactElement {
  return (
    <div
      className={cn(
        "min-h-[12rem] py-4",
        className,
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={typeof message === "string" ? message : "Loading"}
    >
      <span className="sr-only">{message}</span>
      <div aria-hidden="true" className="grid animate-pulse gap-3 motion-reduce:animate-none">
        <div className="h-4 w-32 rounded-control bg-muted" />
        {Array.from({ length: Math.max(1, rows) }, (_, index) => (
          <div
            key={index}
            className={cn(
              "h-14 rounded-control border bg-surface-subtle",
              index === rows - 1 && "w-4/5",
            )}
          />
        ))}
      </div>
    </div>
  );
}

export type PageErrorProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
};

export function PageError({
  title = "Something went wrong",
  description,
  action,
  className,
}: PageErrorProps): React.ReactElement {
  return (
    <div
      className={cn(
        "flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 text-center",
        className,
      )}
      role="alert"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <div className="grid gap-1">
        <h3 className="text-base font-semibold">{title}</h3>
        {description && (
          <p className="mx-auto max-w-md text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
